import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHero } from '../../components/PageHero';
import { Heart, Users, Star, Coffee, X } from 'lucide-react';
export function CareersPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState('');
  const openModal = (jobTitle: string) => {
    setSelectedJob(jobTitle);
    setIsModalOpen(true);
  };
  const values = [{
    icon: <Heart size={32} data-id="element-444" />,
    title: 'Passion',
    desc: 'We love what we do and it shows in every dish we serve.'
  }, {
    icon: <Users size={32} data-id="element-445" />,
    title: 'Community',
    desc: 'We treat our team and guests like family.'
  }, {
    icon: <Star size={32} data-id="element-446" />,
    title: 'Excellence',
    desc: 'We strive for perfection in every detail.'
  }];
  const jobs = [{
    title: 'Executive Sous Chef',
    dept: 'Culinary',
    location: 'Downtown Flagship'
  }, {
    title: 'Event Coordinator',
    dept: 'Private Dining',
    location: 'Corporate Office'
  }, {
    title: 'General Manager',
    dept: 'Operations',
    location: 'Westside Location'
  }, {
    title: 'Marketing Specialist',
    dept: 'Marketing',
    location: 'Corporate Office'
  }];
  return <div className="bg-white" data-id="element-447">
      <PageHero title="Careers & Culture" subtitle="Join a team dedicated to culinary excellence and genuine hospitality." backgroundImage="https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?w=1920&q=80" data-id="element-448" />

      {/* Culture & Values */}
      <section className="py-24 md:py-32" data-id="element-449">
        <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-450">
          <div className="text-center mb-20" data-id="element-451">
            <h2 className="text-4xl md:text-5xl font-serif text-brand-dark mb-6" data-id="element-452">
              Our Core Values
            </h2>
            <div className="w-16 h-1 bg-brand-red mx-auto" data-id="element-453"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-24" data-id="element-454">
            {values.map((val, idx) => <motion.div key={idx} className="text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-455">
                <div className="w-16 h-16 mx-auto bg-brand-beige rounded-full flex items-center justify-center text-brand-terracotta mb-6" data-id="element-456">
                  {val.icon}
                </div>
                <h3 className="text-2xl font-serif text-brand-dark mb-4" data-id="element-457">
                  {val.title}
                </h3>
                <p className="text-brand-dark/70 font-light" data-id="element-458">{val.desc}</p>
              </motion.div>)}
          </div>

          <div className="bg-brand-dark text-white p-12 md:p-20 text-center relative overflow-hidden rounded-sm" data-id="element-459">
            <div className="absolute inset-0 bg-noise opacity-30" data-id="element-460"></div>
            <div className="relative z-10" data-id="element-461">
              <p className="text-2xl md:text-3xl font-serif italic font-light leading-relaxed mb-8" data-id="element-462">
                "Our success is built entirely on the passion and dedication of
                our people. We don't just hire employees; we welcome new members
                to our family."
              </p>
              <p className="text-brand-gold uppercase tracking-[0.2em] text-sm font-medium" data-id="element-463">
                — Alexander Chen, Founder
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 md:py-32 bg-brand-gray/10" data-id="element-464">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-465">
          <div className="text-center mb-16" data-id="element-466">
            <h2 className="text-4xl md:text-5xl font-serif text-brand-dark mb-6" data-id="element-467">
              Open Positions
            </h2>
            <div className="w-16 h-1 bg-brand-dark mx-auto" data-id="element-468"></div>
          </div>

          <div className="space-y-4" data-id="element-469">
            {jobs.map((job, idx) => <motion.div key={idx} className="bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 border border-brand-dark/5 hover:border-brand-gold transition-colors rounded-sm shadow-sm" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-470">
                <div data-id="element-471">
                  <h3 className="text-2xl font-serif text-brand-dark mb-2" data-id="element-472">
                    {job.title}
                  </h3>
                  <div className="flex gap-4 text-sm font-medium text-brand-dark/50 uppercase tracking-wider" data-id="element-473">
                    <span data-id="element-474">{job.dept}</span>
                    <span data-id="element-475">•</span>
                    <span data-id="element-476">{job.location}</span>
                  </div>
                </div>
                <button onClick={() => openModal(job.title)} className="shrink-0 bg-brand-dark text-white px-8 py-3 text-sm uppercase tracking-[0.2em] hover:bg-brand-red transition-colors" data-id="element-477">
                  Apply Now
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Application Modal */}
      <AnimatePresence data-id="element-478">
        {isModalOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-479">
            <motion.div className="absolute inset-0 bg-brand-dark/80 backdrop-blur-sm" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsModalOpen(false)} data-id="element-480" />
            <motion.div className="bg-white w-full max-w-lg p-8 relative z-10 rounded-sm shadow-2xl" initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} data-id="element-481">
              <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 text-brand-dark/50 hover:text-brand-red transition-colors" data-id="element-482">
                <X size={24} data-id="element-483" />
              </button>

              <h3 className="text-3xl font-serif text-brand-dark mb-2" data-id="element-484">
                Apply
              </h3>
              <p className="text-brand-terracotta text-sm uppercase tracking-widest mb-8" data-id="element-485">
                {selectedJob}
              </p>

              <form className="space-y-6" onSubmit={e => e.preventDefault()} data-id="element-486">
                <div data-id="element-487">
                  <label className="block text-xs uppercase tracking-widest text-brand-dark/70 mb-2" data-id="element-488">
                    Full Name
                  </label>
                  <input type="text" className="w-full border-b border-brand-dark/20 py-2 focus:outline-none focus:border-brand-gold transition-colors" data-id="element-489" />
                </div>
                <div data-id="element-490">
                  <label className="block text-xs uppercase tracking-widest text-brand-dark/70 mb-2" data-id="element-491">
                    Email
                  </label>
                  <input type="email" className="w-full border-b border-brand-dark/20 py-2 focus:outline-none focus:border-brand-gold transition-colors" data-id="element-492" />
                </div>
                <div data-id="element-493">
                  <label className="block text-xs uppercase tracking-widest text-brand-dark/70 mb-2" data-id="element-494">
                    LinkedIn URL or Portfolio
                  </label>
                  <input type="url" className="w-full border-b border-brand-dark/20 py-2 focus:outline-none focus:border-brand-gold transition-colors" data-id="element-495" />
                </div>
                <button className="w-full bg-brand-dark text-white py-4 text-sm uppercase tracking-[0.2em] hover:bg-brand-red transition-colors mt-4" data-id="element-496">
                  Submit Application
                </button>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}